import { RestoreOutlined } from '@mui/icons-material'
import { Box, Checkbox, Grid, Typography, useTheme } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useGetComponentsOverviewCounts } from 'src/api/components'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import { GridActionButton } from 'src/components/common/GridActionButton'
import BasicDatePicker from 'src/components/DateTimePicker'
import { IComponentFilters } from 'src/generated-types'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { getUTCDate } from 'src/utils/function'
import ComponentOverviewTableContainer from './ComponentOverviewTableContainer'

const ComponentOverviewContainer = () => {
  const { id: customerId } = useParams()

  const oneYearAhead = new Date()
  oneYearAhead.setFullYear(new Date().getFullYear() + 1)

  const [filters, setFilters] = useState<IComponentFilters>({
    startDate: getUTCDate(new Date()) as Date,
    endDate: getUTCDate(oneYearAhead) as Date,
    showWithoutInstallationDate: false,
    showCurrentlyExpired: false,
  })

  const { t: translate } = useTranslation(['documents', 'common'])
  const theme = useTheme()
  const { data: overviewCounts } = useGetComponentsOverviewCounts(customerId as EntityId, filters)

  const resetFilters = () => {
    setFilters({
      startDate: null,
      endDate: null,
      showWithoutInstallationDate: false,
      showCurrentlyExpired: false,
      facility: '',
      position: '',
      articleNumber: '',
      articleName: '',
      quantity: undefined,
      batchNumber: '',
      installedDate: null,
      lifespanLeft: undefined,
    })
  }

  return (
    <CommonPageLayout titleSection={{ title: translate('expiring-components', { ns: 'component' }) }}>
      <Grid
        display={'flex'}
        flexDirection={'row'}
        gap={1}
        border={`1px solid ${getThemeColor(theme, MUIThemeColors.secondaryLight)}`}
        padding={1}
        borderRadius={1}
        marginBottom={1}
      >
        <BasicDatePicker
          name="start-date"
          isWithoutAppForm
          label={translate('from-date', { ns: 'component' })}
          value={filters.startDate}
          onChange={date =>
            setFilters(prev => ({ ...prev, startDate: date ? (getUTCDate(new Date(date)) as Date) : null }))
          }
        />
        <BasicDatePicker
          name="end-date"
          isWithoutAppForm
          label={translate('to-date', { ns: 'component' })}
          value={filters.endDate}
          onChange={date =>
            setFilters(prev => ({ ...prev, endDate: date ? (getUTCDate(new Date(date)) as Date) : null }))
          }
        />
        <Box display={'flex'} alignItems={'center'}>
          <Checkbox
            checked={filters.showWithoutInstallationDate}
            onClick={() =>
              setFilters(prev => ({ ...prev, showWithoutInstallationDate: !prev.showWithoutInstallationDate }))
            }
          />

          <Typography sx={{ color: theme.palette.primaryDark[theme.palette.mode] }} variant="subtitle2">
            {translate('show-components-without-installation-date', { ns: 'component' }) +
              '(' +
              (overviewCounts?.withoutInstallationDateCount ?? 0) +
              ')'}
          </Typography>
        </Box>
        <Box display={'flex'} alignItems={'center'}>
          <Checkbox
            checked={filters.showCurrentlyExpired}
            onClick={() => setFilters(prev => ({ ...prev, showCurrentlyExpired: !prev.showCurrentlyExpired }))}
            color="error"
          />

          <Typography sx={{ color: theme.palette.error[theme.palette.mode] }} variant="subtitle2">
            {translate('show-currently-expired-components', { ns: 'component' }) +
              '(' +
              (overviewCounts?.expiredCount ?? 0) +
              ')'}
          </Typography>
        </Box>
        <Box display={'flex'} alignItems={'center'} marginLeft={'auto'}>
          <GridActionButton
            height="44px"
            width="44px"
            buttonIcon={<RestoreOutlined />}
            toolTipTitle={translate('reset-filters', { ns: 'component' })}
            onClick={resetFilters}
          />
        </Box>
      </Grid>
      <Box padding={'8px'} bgcolor={getThemeColor(theme, MUIThemeColors.white)} borderRadius={'4px'}>
        <ComponentOverviewTableContainer
          customerId={customerId as EntityId}
          filters={filters}
          setFilters={setFilters}
        />
      </Box>
    </CommonPageLayout>
  )
}

export default ComponentOverviewContainer
